import { useMemo, useState } from "react";
import { CampoNumero, CampoSelect, Pestanas, Resultado, Dato, Rejilla, mxn } from "../components/campos.jsx";
import { calcularInteres } from "../lib/calc.js";

export default function Interes() {
  const [capital, setCapital] = useState(10000);
  const [tasa, setTasa] = useState(10);
  const [plazo, setPlazo] = useState(5);
  const [unidad, setUnidad] = useState("years");
  const [tipo, setTipo] = useState("compound");
  const [capitalizaciones, setCapitalizaciones] = useState("12");

  const r = useMemo(
    () =>
      calcularInteres({
        capital,
        tasaAnual: tasa,
        plazo,
        unidadPlazo: unidad,
        tipo,
        capitalizacionesPorAnio: Number(capitalizaciones),
      }),
    [capital, tasa, plazo, unidad, tipo, capitalizaciones],
  );

  const etiquetaPeriodo = unidad === "years" ? "Año" : "Mes";

  return (
    <div className="space-y-6">
      <Pestanas
        valor={tipo}
        onChange={setTipo}
        opciones={[
          { valor: "compound", etiqueta: "Interés compuesto" },
          { valor: "simple", etiqueta: "Interés simple" },
        ]}
      />

      <Rejilla>
        <div className="space-y-4">
          <CampoNumero label="Capital inicial" valor={capital} onChange={setCapital} prefijo="$" min={0} />
          <CampoNumero label="Tasa de interés anual" valor={tasa} onChange={setTasa} sufijo="%" min={0} />
          <div className="grid grid-cols-2 gap-3">
            <CampoNumero label="Plazo" valor={plazo} onChange={setPlazo} min={0} />
            <CampoSelect
              label="Unidad"
              valor={unidad}
              onChange={setUnidad}
              opciones={[
                { valor: "years", etiqueta: "Años" },
                { valor: "months", etiqueta: "Meses" },
              ]}
            />
          </div>
          {tipo === "compound" && (
            <CampoSelect
              label="Capitalización"
              valor={capitalizaciones}
              onChange={setCapitalizaciones}
              opciones={[
                { valor: "1", etiqueta: "Anual" },
                { valor: "2", etiqueta: "Semestral" },
                { valor: "4", etiqueta: "Trimestral" },
                { valor: "12", etiqueta: "Mensual" },
                { valor: "365", etiqueta: "Diaria" },
              ]}
              ayuda="Cuántas veces al año se suman los intereses al capital."
            />
          )}
        </div>

        <div className="order-first space-y-3 lg:order-last lg:col-span-2">
          <Resultado label="Monto final" valor={mxn(r.total)} tono="positivo" />
          <div className="tarjeta">
            <Dato label="Capital aportado" valor={mxn(capital)} />
            <Dato label="Intereses generados" valor={mxn(r.interes)} />
            <Dato label="Total" valor={mxn(r.total)} destacar />
            <Dato
              label="Multiplicador"
              valor={capital > 0 ? `${(r.total / capital).toFixed(2)}×` : "—"}
            />
          </div>

          {r.tabla.length > 0 && (
            <details className="tarjeta">
              <summary className="cursor-pointer text-sm font-medium texto-2">
                Ver cronograma de saldos ({r.tabla.length} periodos)
              </summary>
              <div className="mt-3 max-h-72 overflow-auto">
                <table className="w-full text-left text-2xs">
                  <thead className="sticky top-0 superficie texto-3">
                    <tr>
                      <th className="py-1.5 pr-2 font-semibold">{etiquetaPeriodo}</th>
                      <th className="py-1.5 pr-2 text-right font-semibold">Saldo inicial</th>
                      <th className="py-1.5 pr-2 text-right font-semibold">Interés</th>
                      <th className="py-1.5 text-right font-semibold">Saldo final</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono texto-2">
                    {r.tabla.map((f) => (
                      <tr key={f.periodo} className="border-t borde">
                        <td className="py-1.5 pr-2">{f.periodo}</td>
                        <td className="py-1.5 pr-2 text-right">{mxn(f.saldoInicial, 0)}</td>
                        <td className="py-1.5 pr-2 text-right text-[color:var(--positivo)]">{mxn(f.interes, 0)}</td>
                        <td className="py-1.5 text-right text-[color:var(--texto)]">{mxn(f.saldoFinal, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      </Rejilla>
    </div>
  );
}
